// ============================================================
// ACCOMMODATION PAYLOAD - BACKEND RECORD -> FRONTEND FORM DATA
//
// This is what was missing before: buildAccommodationPayload()
// (./toBackend.js) only ever ran forward, so when the edit
// form loaded an existing accommodation, the raw backend
// record was dumped straight into formData with none of the
// backend's shapes converted back to what each step's UI
// actually reads. This function is the symmetric reverse -
// call it once with the fetched accommodation record and hand
// the result straight to <AccommodationForm initialData=... />.
//
// A few fields are genuinely one-way and can't be perfectly
// restored because the backend never stored the extra detail
// in the first place (documented inline, file by file, where
// it happens) - those are pre-existing gaps in what gets
// persisted, not something a mapping function can fix without
// a backend schema change.
// ============================================================

import { cleanString, numberOrNull, stringArray } from './helpers';
import { reverseAddress } from './address';
import { reverseAmenities } from './amenities';
import { reverseServices } from './services';
import { reverseUnit } from './units';
import { reversePolicies, to24h } from './policies';
import { reverseSeasonalPricing } from './pricing';
import { reverseMedia, reversePaymentOptions } from './media';
import { reverseNearbyPlaces, reverseFaqs } from './additional';

export function buildFormDataFromAccommodation(accommodation = {}) {
    const source = accommodation || {};

    const units = Array.isArray(source.units) ? source.units.map(reverseUnit) : [];

    const checkInInstructions = source.checkInInstructions || {};

    // extra Additional-info fields (backend: additionalInfo)
    const info = source.additionalInfo || {};

    return {
        /* ========================================================
       BASIC
    ======================================================== */

        title: cleanString(source.title),
        slug: cleanString(source.slug),
        overview: cleanString(source.overview),
        description: cleanString(source.description),

        propertyType: cleanString(source.propertyType),
        propertyCategory: cleanString(source.propertyCategory),

        maxGuests: numberOrNull(source.maxGuests) || 0,
        entirePropertyAvailable: Boolean(source.entirePropertyAvailable),

        totalBedrooms: numberOrNull(source.totalBedrooms) || 0,
        totalBathrooms: numberOrNull(source.totalBathrooms) || 0,
        totalUnits: numberOrNull(source.totalUnits) || units.length || 0,

        highlights: stringArray(source.propertyHighlights),
        languages: stringArray(source.languagesSpoken),

        /* ========================================================
       LOCATION
    ======================================================== */

        address: reverseAddress(source.address),

        /* ========================================================
       HOST
       (organizerId/organizerName are backend/system-controlled -
       never editable from this form, so nothing to reverse-map)
    ======================================================== */

        host: {
            organizerId: cleanString(source.organizerId?._id || source.organizerId)
        },

        /* ========================================================
       UNITS
    ======================================================== */

        units,

        /* ========================================================
       AMENITIES
    ======================================================== */

        amenities: reverseAmenities(source.amenities),

        /* ========================================================
       SERVICES
    ======================================================== */

        services: reverseServices(source.services),

        /* ========================================================
       POLICIES
    ======================================================== */

        // the Policies step edits the booking rules; bookingSettings holds the
        // values search and booking actually use, so show those
        policies: {
            ...reversePolicies(source.policies),
            ...(source.bookingSettings?.advanceBookingDays !== undefined && {
                advanceBookingDays: numberOrNull(source.bookingSettings.advanceBookingDays) ?? 365
            }),
            ...(source.bookingSettings?.allowSameDayBooking !== undefined && {
                sameDayBooking: Boolean(source.bookingSettings.allowSameDayBooking)
            }),
            ...(source.bookingSettings?.instantBooking !== undefined && {
                instantBooking: Boolean(source.bookingSettings.instantBooking)
            })
        },

        /* ========================================================
       MEDIA
    ======================================================== */

        media: reverseMedia(source.media),

        cardImages: stringArray(source.cardImages),

        videos: stringArray(source.videos),

        /* ========================================================
       BOOKING SETTINGS
       (kept in sync with policies rather than the separate,
       effectively-unused bookingSettings object - see the note
       in policies.js's transformBookingSettings)
    ======================================================== */

        bookingSettings: {
            instantBooking: Boolean(source.bookingSettings?.instantBooking),
            requestToBook: source.bookingSettings?.hostApprovalRequired !== false,
            minimumStay: numberOrNull(source.policies?.minStayNights) || 1,
            maximumStay: numberOrNull(source.policies?.maxStayNights),
            advanceBookingDays: numberOrNull(source.bookingSettings?.advanceBookingDays) ?? 365,
            sameDayBooking: Boolean(source.bookingSettings?.allowSameDayBooking),
            // not edited in the form — kept as saved
            minimumAge: numberOrNull(source.bookingSettings?.minimumAge) || 0,
            allowPartialPayment: Boolean(source.bookingSettings?.allowPartialPayment),
            partialPaymentPercent: numberOrNull(source.bookingSettings?.partialPaymentPercent) || 0
        },

        /* ========================================================
       PAYMENT
    ======================================================== */

        paymentOptions: reversePaymentOptions(source.paymentOptions),

        /* ========================================================
       ADDITIONAL
    ======================================================== */

        inclusions: stringArray(source.inclusions),
        exclusions: stringArray(source.exclusions),
        thingsToCarry: stringArray(source.thingsToCarry),
        safetyMeasures: stringArray(source.safetyMeasures),
        terms: stringArray(source.terms),

        faqs: reverseFaqs(source.faqs),

        nearbyPlaces: reverseNearbyPlaces(source.nearbyPlaces),

        accessibility: stringArray(source.accessibility),

        /*
         * checkInInstructions.instructions is a single free-text
         * blob that AdditionalInformation.jsx built by joining
         * SEVERAL separate fields together at submit time
         * (self check-in flag, guest info, parking, transport,
         * notes - see buildInstructionsText() in that file).
         * There's no reliable way to split that blob back into
         * those original fields, so it's placed as-is into the
         * "Check-in instructions" textarea (additional.
         * checkInInstructions) rather than being dropped - the
         * organizer will see everything they wrote, just not
         * pre-sorted back into the original smaller boxes.
         * checkInTime/checkOutTime DO have their own backend
         * columns, so checkInFrom/checkOutFrom restore correctly.
         */
        additional: {
            // <input type="time"> needs "14:00" (older records stored "2:00 PM")
            checkInFrom: to24h(checkInInstructions.checkInTime),
            checkInUntil: to24h(info.checkInUntil),
            checkOutFrom: to24h(checkInInstructions.checkOutTime),
            checkOutUntil: to24h(info.checkOutUntil),
            selfCheckIn: Boolean(info.selfCheckIn),
            checkInInstructions: cleanString(checkInInstructions.instructions),
            guestInformation: cleanString(info.guestInformation),
            parkingType: cleanString(info.parkingType),
            parkingCapacity: info.parkingCapacity ?? '',
            transportationInfo: cleanString(info.transportationInfo),
            notes: cleanString(info.notes),
            // ticked boxes (saved as a list of keys)
            accessibility: Object.fromEntries(stringArray(source.accessibility).map((key) => [key, true]))
        },

        checkInInstructions: {
            checkInTime: cleanString(checkInInstructions.checkInTime),
            checkOutTime: cleanString(checkInInstructions.checkOutTime),
            instructions: cleanString(checkInInstructions.instructions)
        },

        /* ========================================================
       SEO
    ======================================================== */

        seo: {
            metaTitle: cleanString(source.seo?.metaTitle),
            metaDescription: cleanString(source.seo?.metaDescription),
            keywords: stringArray(source.seo?.keywords),
            canonicalUrl: cleanString(source.seo?.canonicalUrl)
        },

        /* ========================================================
       SEASONAL PRICING
    ======================================================== */

        seasonalPricing: reverseSeasonalPricing(source.seasonalPricing),

        seasonalAvailability: []
    };
}

export default buildFormDataFromAccommodation;
